/**
 * Which `/lang` spellings a URL may carry, and which pages accept nothing else
 * (SIIL-6356, follow-up to SIIL-6220).
 *
 * Every route that takes a language does so through ONE trailing
 * `lang/:language` pair, so a URL whose `/lang` part is anything else is junk:
 *
 *   /press/lang               bare `lang`, no code
 *   /press/lang/lang/es       doubled — what Wistia's relative links produced
 *   /press/lang/es/lang/en    two pairs
 *
 * Left alone, `removeLanguageFromURL` only stripped the LAST pair, so a bare or
 * doubled segment survived into the canonical and every hreflang alternate
 * (`/press/lang` advertised `/press/lang/lang/es`), and each variant answered
 * 200 with itself as canonical.
 *
 * `SiilaTranslationService.getLangUrlRedirect` applies these rules and
 * `appInitializerFactory` 301s to its answer at boot, before anything reads the
 * URL.
 *
 * Pure data + pure functions — no Angular, no `window` — mirroring
 * `academy-routes.ts`.
 */

/** The literal segment that introduces a `/lang/<code>` suffix. */
export const LANG_SEGMENT = "lang";

/**
 * Static pages that render ONLY at their clean path and at `/lang/<code>` for a
 * language the deployment publishes besides its default. Any other `/lang…`
 * spelling — an unsupported or default-language code, a junk segment — 301s to
 * the clean path (or to `/lang/<code>` when a valid code is present). Any other
 * sub-path has no route and answers 404.
 */
export const STRICT_LANG_PAGES: string[] = [
  "/press",
  "/terms-of-use",
  "/privacy-policy"
];

/**
 * Pages that used to take a bare code with no `lang` segment
 * (`/privacy-policy/en`, `/terms-of-use/pt`) through a `:lang` route. The route
 * is gone; a known code still 301s to its localized URL instead of a 404,
 * because those links may live outside this app.
 */
export const LEGACY_CODE_SUFFIX_PAGES: string[] = [
  "/terms-of-use",
  "/privacy-policy"
];

/**
 * `path` with its first `lang` segment and everything after it removed — the
 * language-free page path. Safe on full URLs (`https://host/...`): no host or
 * scheme part is ever a bare `lang` segment.
 */
export function cutAtLangSegment(path: string): string {
  if (!path) {
    return path;
  }
  const segments = path.split("/");
  const index = segments.findIndex(
    segment => segment.toLowerCase() === LANG_SEGMENT
  );
  return index === -1 ? path : segments.slice(0, index).join("/");
}
